/* ============================================================
   BNE STUDIO — Midnight Blueprint page shell
   Reusable wrapper for every product page in the new design
   system. Provides the scoped `.night` canvas (blueprint grid +
   glow live on this element's ::before/::after), the shared nav
   and footer, and the motion engine. Drop page content as
   children; pass `active` to highlight the current product.
   ============================================================ */
import React, { useEffect, useRef } from "react";
import NightNav from "./NightNav";
import NightFooter from "./NightFooter";
import useNightMotion from "./useNightMotion";
import "../../styles/night.css";
import "../../styles/night-product.css";

// `variant` adds a page-specific root class (e.g. "pg-home") so a page's own
// CSS file can scope overrides of SHARED classes (.phero-grid, .chip…) to just
// that page — CRA bundles all CSS globally, so unscoped `.night .x` overrides
// would otherwise leak onto every page.
// `description` sets the page's <meta name="description"> while mounted.
export default function NightLayout({ active = "", variant = "", description, children }) {
  const rootRef = useRef(null);
  useNightMotion(rootRef);

  useEffect(() => {
    if (!description) return undefined;
    let meta = document.querySelector('meta[name="description"]');
    let created = false;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
      created = true;
    }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", description);
    return () => {
      if (created) meta.remove();
      else if (prev != null) meta.setAttribute("content", prev);
    };
  }, [description]);

  return (
    <div className={variant ? `night ${variant}` : "night"} ref={rootRef}>
      <NightNav active={active} />
      {children}
      <NightFooter active={active} />
    </div>
  );
}
